import React from 'react';
import { 
  Row, Col, Card, Statistic, Progress, Tag, Tooltip, Empty, 
  Typography, Space, Alert
} from 'antd';
import { 
  CheckCircleOutlined, 
  FieldTimeOutlined, 
  CloseCircleOutlined, 
  CarOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ReferenceLine,
  ResponsiveContainer 
} from 'recharts';
import dayjs from 'dayjs';
import { OTR_THRESHOLDS } from '../utils/OTRDataService';
import otrDataProcessor from '../utils/OTRDataProcessor';

const { Text } = Typography;

const OTRPerformanceOverviewTab = ({ 
  stopData, 
  selectedStopTypes, 
  setSelectedStopTypes, 
  dateRange, 
  tripData,
  shouldTreatEarlyAsOnTime,
  treatSchoolOutEarlyAsOnTime,
  showTrackedOnly
}) => {
  // Use centralized data processor for all calculations
  const aggregatedStats = otrDataProcessor.calculateAggregatedStats(
    stopData, 
    selectedStopTypes, 
    treatSchoolOutEarlyAsOnTime
  );

  // Handle stop type selection change
  const handleStopTypeChange = (types) => {
    // Ensure at least one option is selected
    if (types.length === 0) {
      setSelectedStopTypes(['F']); // Default to "First" if nothing selected
      return;
    }
    
    // Handle "All" option
    if (types.includes('ALL')) {
      // If "All" was just selected, make it the only selection
      if (!selectedStopTypes.includes('ALL')) {
        setSelectedStopTypes(['ALL']);
      } else {
        // If other options were also selected, remove "All"
        const otherTypes = types.filter(type => type !== 'ALL');
        if (otherTypes.length > 0) {
          setSelectedStopTypes(otherTypes);
        } else {
          // Keep "All" if it's the only selection
          setSelectedStopTypes(['ALL']);
        }
      }
    } else {
      setSelectedStopTypes(types);
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row gutter={16}>
              <Col span={5}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>On-Time Performance</span>
                        <Tooltip title={`Percentage of buses that arrive between ${Math.abs(OTR_THRESHOLDS.early / 60)} minutes early and ${OTR_THRESHOLDS.late / 60} minutes late across all stops. This is calculated by dividing the number of on-time arrivals by the total number of monitored arrivals.`}>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={Math.round(aggregatedStats.onTimePercent)}
                    suffix="%"
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={5}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Early Departures</span>
                        <Tooltip title={`Percentage of buses that depart more than ${Math.abs(OTR_THRESHOLDS.early / 60)} minutes before the scheduled time. Early departures may cause passengers to miss their bus.`}>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={Math.round(aggregatedStats.earlyPercent)}
                    suffix="%"
                    valueStyle={{ color: '#fa541c' }}
                    prefix={<FieldTimeOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={5}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Late Departures</span>
                        <Tooltip title={`Percentage of buses that depart more than ${OTR_THRESHOLDS.late / 60} minutes after the scheduled time. Late departures often impact the rest of the route and connecting services.`}>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={Math.round(aggregatedStats.latePercent)}
                    suffix="%"
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={5}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Records Analysed</span>
                        <Tooltip title={`Total number of stop records included in the analysis. ${showTrackedOnly ? 'Only records with actual arrival/departure times are included.' : 'All records including untracked ones are included.'}`}>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={aggregatedStats.total?.toLocaleString() || 0}
                    prefix={<CarOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={4}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>With Tracking</span>
                        <Tooltip title="Number of records that have actual arrival or departure times and can be used for OTR calculations.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={aggregatedStats.stopsWithVariance?.toLocaleString() || 0}
                    valueStyle={{ color: aggregatedStats.stopsWithVariance === aggregatedStats.total ? '#3f8600' : '#fa8c16' }}
                    prefix={<FieldTimeOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            {/* OTR Trend Section */}
            <Row gutter={16} style={{ marginTop: '16px' }}>
              <Col span={18}>
                <Card 
                  title={
                    <Space>
                      <span>Daily OTR Performance</span>
                      <Tooltip title="Shows the daily on-time running performance across the selected date range. Each bar represents a single day.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  {(() => {
                    if (!stopData || stopData.length === 0) return <Empty description="No data available" />;
                    
                    // Apply the same filtering as aggregated stats
                    const filteredStopData = otrDataProcessor.filterDataByStopType(stopData, selectedStopTypes);
                    
                    if (filteredStopData.length === 0) {
                      return <Empty description="No data available for selected stop types" />;
                    }
                    
                    // Group data by date to calculate daily OTR
                    const dailyOTR = {};
                    
                    filteredStopData.forEach(record => {
                      const dateId = record.DATE_ID;
                      if (!dateId) return;
                      
                      const dateStr = String(dateId);
                      if (!dailyOTR[dateStr]) {
                        dailyOTR[dateStr] = {
                          date: dateStr,
                          displayDate: record.DATE_FORMATTED || dayjs(dateStr).format('YYYY-MM-DD'),
                          totalStops: 0,
                          onTimeStops: 0,
                          earlyStops: 0,
                          lateStops: 0,
                          departureVarianceCount: 0,
                          arrivalVarianceCount: 0,
                          noVarianceCount: 0,
                          stopsWithVariance: 0
                        };
                      }
                      
                      dailyOTR[dateStr].totalStops++;
                      
                      // Calculate on-time status
                      let variance = null;
                      if (Number.isFinite(record.DEPARTURE_TIME_VARIANCE)) {
                        variance = record.DEPARTURE_TIME_VARIANCE;
                        dailyOTR[dateStr].departureVarianceCount++;
                      } else if (Number.isFinite(record.A_DEP) && Number.isFinite(record.S_DEP)) {
                        variance = record.A_DEP - record.S_DEP;
                        dailyOTR[dateStr].departureVarianceCount++;
                      } else if (Number.isFinite(record.ARRIVAL_TIME_VARIANCE)) {
                        variance = record.ARRIVAL_TIME_VARIANCE;
                        dailyOTR[dateStr].arrivalVarianceCount++;
                      } else {
                        dailyOTR[dateStr].noVarianceCount++;
                      }
                      
                      if (variance !== null) {
                        dailyOTR[dateStr].stopsWithVariance++;
                        
                        if (variance > OTR_THRESHOLDS.early && variance < OTR_THRESHOLDS.late) {
                          dailyOTR[dateStr].onTimeStops++;
                        } else if (variance <= OTR_THRESHOLDS.early) {
                          dailyOTR[dateStr].earlyStops++;
                        } else {
                          dailyOTR[dateStr].lateStops++;
                        }
                      }
                    });
                    
                    // Convert to array and sort by date
                    const dailyOTRArray = Object.values(dailyOTR)
                      .map(day => ({
                        ...day,
                        onTimePercent: day.stopsWithVariance > 0 ? (day.onTimeStops / day.stopsWithVariance) * 100 : 0
                      }))
                      .sort((a, b) => a.date.localeCompare(b.date));
                    

                    
                    // Ensure all dates in the selected range are included
                    const allDates = [];
                    if (dateRange && dateRange[0] && dateRange[1]) {
                      let currentDate = dateRange[0].clone();
                      const endDate = dateRange[1].clone();
                      
                      while (currentDate.isSameOrBefore(endDate, 'day')) {
                        const dateStr = currentDate.format('YYYYMMDD');
                        if (!dailyOTR[dateStr]) {
                          allDates.push({
                            date: dateStr,
                            displayDate: currentDate.format('YYYY-MM-DD'),
                            totalStops: 0,
                            onTimeStops: 0,
                            earlyStops: 0,
                            lateStops: 0,
                            departureVarianceCount: 0,
                            arrivalVarianceCount: 0,
                            noVarianceCount: 0,
                            stopsWithVariance: 0,
                            onTimePercent: 0
                          });
                        }
                        currentDate = currentDate.add(1, 'day');
                      }
                    }
                    
                    // Combine existing data with placeholder data for missing dates
                    const completeOTRArray = [...dailyOTRArray, ...allDates]
                      .sort((a, b) => {
                        const dateA = dayjs(a.date);
                        const dateB = dayjs(b.date);
                        return dateA.valueOf() - dateB.valueOf();
                      });
                    
                    // Prepare chart data
                    const chartData = completeOTRArray.map(day => {
                      let displayDate;
                      try {
                        const dateObj = dayjs(day.date);
                        if (dateObj.isValid()) {
                          displayDate = dateObj.format('DD/MM');
                        } else {
                          displayDate = day.displayDate;
                        }
                      } catch (e) {
                        displayDate = day.displayDate;
                      }
                      
                      return {
                        date: displayDate,
                        onTimeValue: Math.round(day.onTimePercent),
                        hasData: day.totalStops > 0,
                        totalObservations: day.totalStops,
                        departureCount: day.departureVarianceCount,
                        arrivalCount: day.arrivalVarianceCount,
                        noVarianceCount: day.noVarianceCount,
                        originalDate: day.date
                      };
                    });
                    
                    return (
                      <div style={{ height: '350px', width: '100%', marginLeft: '-15px' }}>
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%" minWidth={600}>
                            <BarChart
                              data={chartData}
                              margin={{ top: 25, right: 20, left: 0, bottom: 25 }}
                              barSize={45}
                              barGap={0}
                              maxBarSize={120}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="date" 
                                angle={0} 
                                textAnchor="middle"
                                height={40}
                                tickMargin={5}
                                axisLine={false}
                                tick={{ fontSize: 12, fill: '#666' }}
                                interval="preserveStartEnd"
                                tickLine={false}
                              />
                              <YAxis 
                                domain={[0, 100]}
                                axisLine={{ stroke: '#e5e5e5' }}
                                tick={{ fontSize: 11, fill: '#666' }}
                                tickLine={false}
                                width={20}
                                padding={{ left: 0 }}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => {
                                  let status = 'Poor';
                                  let color = '#f5222d';
                                  
                                  if (value >= 95) {
                                    status = 'Excellent';
                                    color = '#52c41a';
                                  } else if (value >= 75) {
                                    status = 'Acceptable';
                                    color = '#faad14';
                                  }
                                  
                                  return [
                                    <span style={{ color }}>{`${value}% (${status})`}</span>, 
                                    'On Time'
                                  ];
                                }}
                                labelFormatter={(label, payload) => {
                                  if (payload && payload.length > 0) {
                                    const entry = payload[0].payload;
                                    return (
                                      <span style={{ display: 'block' }}>
                                        <span style={{ display: 'block' }}><strong>Date: {label}</strong></span>
                                        <span style={{ display: 'block', marginTop: '8px', fontSize: '11px' }}>
                                          <span style={{ display: 'block' }}>Total Observations: {entry.totalObservations}</span>
                                          <span style={{ display: 'block' }}>Departure Time: {entry.departureCount} records ({Math.round((entry.departureCount/entry.totalObservations)*100) || 0}%)</span>
                                          <span style={{ display: 'block' }}>Arrival Time: {entry.arrivalCount} records ({Math.round((entry.arrivalCount/entry.totalObservations)*100) || 0}%)</span>
                                          <span style={{ display: 'block' }}>No Variance: {entry.noVarianceCount} records ({Math.round((entry.noVarianceCount/entry.totalObservations)*100) || 0}%)</span>
                                        </span>
                                      </span>
                                    );
                                  }
                                  return `Date: ${label}`;
                                }}
                                contentStyle={{ 
                                  borderRadius: '4px', 
                                  border: 'none', 
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  minWidth: '200px'
                                }}
                              />
                              <ReferenceLine 
                                y={95} 
                                stroke="#52c41a" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '95', 
                                  position: 'right',
                                  fill: '#52c41a',
                                  fontSize: 12
                                }}
                              />
                              <ReferenceLine 
                                y={75} 
                                stroke="#faad14" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '75', 
                                  position: 'right',
                                  fill: '#faad14',
                                  fontSize: 12
                                }}
                              />
                              <ReferenceLine 
                                y={50} 
                                stroke="#f5222d" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '50', 
                                  position: 'right',
                                  fill: '#f5222d',
                                  fontSize: 12
                                }}
                              />
                              <Bar 
                                dataKey="onTimeValue" 
                                name="On Time" 
                                fill="#52c41a"
                                radius={[4, 4, 0, 0]}
                                shape={(props) => {
                                  if (!props.payload.hasData) {
                                    return null;
                                  }
                                  
                                  let fill = '#f5222d';
                                  
                                  if (props.payload.onTimeValue >= 95) {
                                    fill = '#52c41a';
                                  } else if (props.payload.onTimeValue >= 75) {
                                    fill = '#faad14';
                                  }
                                  
                                  return (
                                    <g>
                                      <rect
                                        x={props.x}
                                        y={props.y}
                                        width={props.width}
                                        height={props.height}
                                        fill={fill}
                                        radius={[4, 4, 0, 0]}
                                        rx={4}
                                      />
                                      <text
                                        x={props.x + props.width / 2}
                                        y={props.y - 10}
                                        textAnchor="middle"
                                        fill={fill}
                                        fontSize={11}
                                        fontWeight="bold"
                                        fontFamily="'Helvetica Neue', Arial, sans-serif"
                                      >
                                        {props.payload.onTimeValue}%
                                      </text>
                                    </g>
                                  );
                                }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Empty description="No data available" />
                        )}
                      </div>
                    );
                  })()}
                </Card>
              </Col>

              <Col span={6}>
                <Card
                  title={
                    <Space>
                      <span>Performance Trend</span>
                      <Tooltip title="Shows the trend in on-time performance between the first and second half of the selected period. A positive value indicates improvement.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  style={{ height: '100%' }}
                >
                  {(() => {
                    if (!stopData || stopData.length === 0) return <Empty description="No data available" />;
                    
                    // Use centralized data processor for daily performance
                    const dailyOTR = otrDataProcessor.calculateDailyPerformance(
                      stopData, 
                      selectedStopTypes, 
                      treatSchoolOutEarlyAsOnTime
                    );
                    
                    // Convert to array and sort by date (percentages already calculated by processor)
                    const dailyOTRArray = Object.values(dailyOTR)
                      .sort((a, b) => a.date.localeCompare(b.date));
                    
                    // Add trend analysis by comparing first and second half
                    const midpoint = Math.floor(dailyOTRArray.length / 2);
                    let firstHalfOTR = 0;
                    let secondHalfOTR = 0;
                    
                    if (dailyOTRArray.length > 0) {
                      const firstHalf = dailyOTRArray.slice(0, midpoint);
                      const secondHalf = dailyOTRArray.slice(midpoint);
                      
                      firstHalfOTR = firstHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / firstHalf.length;
                      secondHalfOTR = secondHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / secondHalf.length;
                    }
                    
                    const trendPercentage = firstHalfOTR > 0 
                      ? ((secondHalfOTR - firstHalfOTR) / firstHalfOTR) * 100 
                      : 0;
                    
                    const trendText = trendPercentage >= 0 
                      ? `+${trendPercentage.toFixed(1)}%` 
                      : `${trendPercentage.toFixed(1)}%`;
                    
                    const isPositive = trendPercentage >= 0;
                    
                    // Add labels to indicate which stop types are being shown
                    const stopTypeLabels = {
                      'ALL': 'All Stops',
                      'F': 'First Stops',
                      'M': 'Mid Stops',
                      'L': 'Last Stops'
                    };
                    
                    const selectedTypeLabels = selectedStopTypes.map(type => stopTypeLabels[type] || type).join(', ');
                    
                    return (
                      <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <Statistic
                          value={trendText}
                          valueStyle={{ 
                            color: isPositive ? '#3f8600' : '#cf1322',
                            fontSize: '36px'
                          }}
                          prefix={isPositive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          suffix={
                            <div style={{ fontSize: '14px', marginTop: '8px' }}>
                              from previous period
                            </div>
                          }
                        />
                        <div style={{ marginTop: '16px' }}>
                          <Text type="secondary">
                            First half: {firstHalfOTR.toFixed(1)}%
                          </Text>
                          <br />
                          <Text type="secondary">
                            Second half: {secondHalfOTR.toFixed(1)}%
                          </Text>
                          <br />
                          <Tag color="blue" style={{ marginTop: '8px' }}>
                            {selectedTypeLabels}
                          </Tag>
                        </div>
                      </div>
                    );
                  })()}
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OTRPerformanceOverviewTab; 